import { FeLoggerClient } from "./client";

import { config } from "@jb-dotcom/config/dotcom";

export type FeLoggerConfig = {
  additionalData?: Partial<{ trustArc: string; homepage: string }>; // This can hold any fields and values
  applicationInfo?: { id: string; version: string };
  userId?: string;
  sessionId?: string;
  loggerSettings: LoggerSettings;
  loggerUrl?: string;
  localStorageId?: string;
  environment?: EnvironmentNames;
  subscriptionKey: string;
};

/**
 * these are the "options" we allow the UI to configure
 */
type LoggerSettings = {
  capturePageLoad: boolean;
  captureHrefChange: boolean;
  captureError: boolean;
  captureClick: boolean;
  enableLoggingToService?: boolean;
  enableConsoleLogs?: boolean;
  batchMessages?: boolean;
};
type EnvironmentNames = "local-dev" | "dev" | "stg" | "qa" | "prd";

declare let addAddtionalData:
  | ((config: Partial<FeLoggerConfig>) => void)
  | undefined;
declare let JBFELoggerSetup: ((config: FeLoggerConfig) => void) | undefined;
declare let logOtherEvent:
  | ((actionType: string, additionalProperties: unknown) => void)
  | undefined;

export type FeLoggerProperties = {
  src: string;
  subscriptionKey: string;
};
export const FeLogger = async ({
  src,
  subscriptionKey,
}: FeLoggerProperties) => {
  const {
    MGNL: { LANGUAGES },
  } = await config();
  const homepagePaths = new Set(["/", ...LANGUAGES.map((lang) => `/${lang}`)]);

  return (
    <FeLoggerClient
      homepagePaths={homepagePaths}
      src={src}
      subscriptionKey={subscriptionKey}
    />
  );
};

let resolveLoad: () => void;

const loaded = new Promise<void>((resolve) => {
  resolveLoad = resolve;
});

export type InitializeLoggerProperties = Omit<
  FeLoggerConfig,
  "loggerSettings"
> &
  Partial<Pick<FeLoggerConfig, "loggerSettings">>;
export const initializeLogger =
  ({ loggerSettings, ...properties }: InitializeLoggerProperties) =>
  () => {
    void logData({ pageViewEvent: "onload" }, "page_view");
    JBFELoggerSetup?.({
      ...properties,
      applicationInfo: {
        id: "DotNext",
        version: process.env.NEXT_PUBLIC_GITHUB_SHA ?? "undefined",
      },
      loggerSettings: {
        ...loggerSettings,
        batchMessages: true,
        captureClick: false,
        captureError: true,
        captureHrefChange: true,
        capturePageLoad: false,
        enableConsoleLogs: false,
      },
    });
    resolveLoad();
  };
/**
 * Use this function to send error logs to CB-Logger service, which continues to Argus
 * @param data any object which you'd like to attach to the automatically generated data
 */
export const logError = async (data: unknown) => {
  await loaded;
  logOtherEvent?.("error", data);
};
/**
 * Use this function to send logs to CB-Logger service, which continues to Argus
 * @param actionType
 *  'page_view', 'click', 'form_submission','login', 'logout', 'search',
 *  'video_play', performance', 'purchase','unknown',
 * @param data any object which you'd like to attach to the automatically generated data
 */
export const logData = async (data: unknown, actionType?: string) => {
  await loaded;
  logOtherEvent?.(actionType ?? "event", data);
};
/**
 * Use this function to update the logger's run config, or to add data to the additionalData field (bucket)
 * @param data any object which you'd like to update
 */
export const update = async (data: Partial<FeLoggerConfig>) => {
  await loaded;
  addAddtionalData?.(data);
};
